<template>
  <form @submit.prevent="saveConfirmation">
    <div class="row">
      <div class="col-12 mb-3">
        <label class="form-label">Order Status</label>
        <input
          type="text"
          class="form-control"
          :value="order?.order_status || 'N/A'"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Total Amount</label>
        <input
          type="text"
          class="form-control"
          :value="formatCurrency(order?.total_amount || 0)"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Supplier</label>
        <div class="row">
          <div class="col-lg-12">
            <select
              v-model="form.supplier_id"
              class="form-select"
              :class="{ 'is-invalid': errors.supplier_id }"
            >
              <option value="">Select Supplier</option>
              <option
                v-for="supplier in suppliers"
                :key="supplier.id"
                :value="supplier.id"
              >
                {{ supplier.name }}
              </option>
            </select>
            <div v-if="errors.supplier_id" class="invalid-feedback">
              {{ errors.supplier_id[0] }}
            </div>
          </div>

        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Cost Price</label>
        <input
          v-model.number="form.cost_price"
          type="number"
          class="form-control"
          :class="{ 'is-invalid': errors.cost_price }"
          step="0.01"
          min="0"
          placeholder="Enter total cost price"
        >
        <div v-if="errors.cost_price" class="invalid-feedback">
          {{ errors.cost_price[0] }}
        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Confirmation Date</label>
        <input
          v-model="form.confirmation_date"
          type="date"
          class="form-control"
          :class="{ 'is-invalid': errors.confirmation_date }"
        >
        <div v-if="errors.confirmation_date" class="invalid-feedback">
          {{ errors.confirmation_date[0] }}
        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Memo</label>
        <input
          v-model="form.memo"
          class="form-control"
          :class="{ 'is-invalid': errors.memo }"
          placeholder="Enter order memo"
          rows="3"
        ></input>
        <div v-if="errors.memo" class="invalid-feedback">
          {{ errors.memo[0] }}
        </div>
      </div>

      <div class="col-12 mb-2">
        <div class="alert alert-info" role="alert">
          <strong>Info:</strong> Confirming this order will change its status to "Ready" and assign it to the selected supplier.
        </div>
      </div>

      <div class="col-12">
        <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">
          Close
        </button>
        <button type="submit" class="btn btn-success" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Confirm Order
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'OrderConfirm',

  props: {
    order: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      form: {
        supplier_id: '',
        cost_price: 0,
        confirmation_date: new Date().toISOString().split('T')[0],
        note: '',
        memo: ''
      },
      suppliers: [],
      errors: {},
      loading: false
    };
  },

  async mounted() {
    console.log('OrderConfirm component mounted');
    console.log('Order data:', this.order);

    await this.loadSuppliers();

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  },

  methods: {
    async loadSuppliers() {
      try {
        const response = await this.$http.get('/api/suppliers');
        if (response.data.success) {
          this.suppliers = response.data.suppliers.data || response.data.suppliers;
        }
      } catch (error) {
        console.error('Failed to load suppliers:', error);
        this.showErrorMessage('Failed to load suppliers');
      }
    },

    async saveConfirmation() {
      this.loading = true;
      this.errors = {};

      try {
        const response = await this.$http.post(`/api/orders/${this.order.id}/confirm`, {
          supplier_id: this.form.supplier_id,
          cost_price: this.form.cost_price,
          confirmation_date: this.form.confirmation_date,
          note: this.form.note,
          memo: this.form.memo
        });

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Reset form
          this.resetForm();

          // Close modal
          const modal = this.$el.closest('.modal');
          if (modal) {
            const modalInstance = bootstrap.Modal.getInstance(modal);
            if (modalInstance) {
              modalInstance.hide();
            }
          }

          // Emit event for parent components
          this.$emit('order-confirmed', response.data.order);

          // Redirect if specified
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            // Refresh page to show updated order
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Order confirmation failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while confirming the order'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    openSupplierModal() {
      // Emit event to open supplier modal
      this.$emit('open-supplier-modal');

      // Or trigger modal directly if available
      const modal = document.getElementById('add-supplier');
      if (modal) {
        const modalInstance = new bootstrap.Modal(modal);
        modalInstance.show();
      }
    },

    resetForm() {
      this.form = {
        supplier_id: '',
        cost_price: 0,
        confirmation_date: new Date().toISOString().split('T')[0],
        note: '',
        memo: ''
      };
      this.errors = {};
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    showSuccessMessage,
    showErrorMessage
  }
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

.add-icon .choose-add {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #666;
  text-decoration: none;
}

.add-icon .choose-add:hover {
  background-color: #f8f9fa;
  color: #007bff;
}

.alert-info {
  border-color: #74b9ff;
  background-color: #e3f2fd;
  color: #2d3436;
}
</style>
